import React, { useState } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { sendDataUploadNotification } from '../utils/email-service';
import Layout from '../components/Layout';
import '../styles/data-preparation.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const DataPreparation = ({ user }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access data preparation services.</p>
        </div>
      </Layout>
    );
  }

  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
    setUploadStatus('');
  };

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file first.');
      return;
    }
    
    if (!userInfo.name || !userInfo.email) {
      setUploadStatus('Please provide your name and email address.');
      return;
    }
    
    // File size limit: 50MB
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (selectedFile.size > maxSize) {
      setUploadStatus('File size must be less than 50MB. For larger files, please contact us directly.');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/json', 'text/plain'];
    if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(csv|xlsx|xls|json|txt)$/i)) {
      setUploadStatus('Please upload only CSV, Excel, JSON, or TXT files.');
      return;
    }
    
    setUploadStatus('Uploading...');
    
    try {
      const fileName = `data-uploads/${Date.now()}-${selectedFile.name}`;
      
      // Upload to Amplify Gen 2 storage
      const result = await uploadData({
        key: fileName,
        data: selectedFile,
        options: {
          contentType: selectedFile.type
        }
      }).result;
      
      console.log('File uploaded successfully:', result);
      
      // Send email notification
      const uploadDetails = {
        fileName: selectedFile.name,
        size: (selectedFile.size / 1024 / 1024).toFixed(2) + ' MB',
        type: selectedFile.type,
        uploadKey: fileName,
        userInfo
      };
      
      try {
        await sendDataUploadNotification(uploadDetails);
        console.log('Email notification sent successfully');
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't fail the upload if email fails
      }
      
      setUploadStatus('File uploaded successfully! We will analyze your data and contact you within 48 hours.');
      setSelectedFile(null);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`Upload failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="data-preparation-page">
        <div className="hero-section">
          <h1>Data Preparation Services</h1>
          <p className="hero-subtitle">Transform your raw data into AI-ready datasets with our expert data preparation services</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>What We Provide</h2>
            <div className="services-grid">
              <div className="service-item">
                <h3>Data Cleansing</h3>
                <p>Remove inconsistencies, duplicates, and errors from your datasets</p>
              </div>
              <div className="service-item">
                <h3>Data Normalization</h3>
                <p>Standardize formats and scales across your data sources</p>
              </div>
              <div className="service-item">
                <h3>Data Transformation</h3>
                <p>Convert data into optimal formats for AI model training</p>
              </div>
              <div className="service-item">
                <h3>Dimensionality Reduction</h3>
                <p>Apply PCA and t-SNE techniques to optimize data structure</p>
              </div>
            </div>
          </section>

          <section className="security-guarantee">
            <h2>Your Data Security is Guaranteed</h2>
            <div className="security-features">
              <div className="security-item">
                <h4>🔒 End-to-End Encryption</h4>
                <p>All data transfers and storage use military-grade encryption</p>
              </div>
              <div className="security-item">
                <h4>🏥 HIPAA Compliant</h4>
                <p>Healthcare data handled with full HIPAA compliance</p>
              </div>
              <div className="security-item">
                <h4>🛡️ GDPR Compliant</h4>
                <p>European data protection standards maintained</p>
              </div>
              <div className="security-item">
                <h4>🗑️ Secure Deletion</h4>
                <p>Data permanently deleted after project completion</p>
              </div>
            </div>
          </section>

          <section className="upload-section">
            <h2>Get Your Complimentary Data Analysis</h2>
            <p>Upload your dataset and receive a free analysis and cleaning report within 48 hours.</p>
            
            <div className="upload-container">
              <div className="contact-form">
                <h3>Your Contact Information</h3>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="Full Name *" 
                    value={userInfo.name}
                    onChange={(e) => handleUserInfoChange('name', e.target.value)}
                    className="form-input"
                  />
                  <input 
                    type="email" 
                    placeholder="Email Address *" 
                    value={userInfo.email}
                    onChange={(e) => handleUserInfoChange('email', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-row">
                  <input 
                    type="text" 
                    placeholder="Company (Optional)" 
                    value={userInfo.company}
                    onChange={(e) => handleUserInfoChange('company', e.target.value)}
                    className="form-input"
                  />
                  <input 
                    type="tel" 
                    placeholder="Phone (Optional)" 
                    value={userInfo.phone}
                    onChange={(e) => handleUserInfoChange('phone', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="upload-area">
                <input 
                  type="file" 
                  id="dataFile" 
                  onChange={handleFileSelect}
                  accept=".csv,.xlsx,.json,.txt"
                  className="file-input"
                />
                <label htmlFor="dataFile" className="file-label">
                  {selectedFile ? `${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)` : 'Choose File (CSV, Excel, JSON, TXT) - Max 50MB'}
                </label>
                <button onClick={handleUpload} className="upload-btn">
                  Upload for Free Analysis
                </button>
                {uploadStatus && <p className="upload-status">{uploadStatus}</p>}
              </div>
              
              <div className="contact-alternative">
                <p>Prefer to discuss your data needs first?</p>
                <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
                <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
              </div>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Data Assessment</h3>
                <p>We analyze your data structure, quality, and potential issues</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Cleaning & Preparation</h3>
                <p>Remove inconsistencies and prepare data for AI training</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Optimization</h3>
                <p>Apply advanced techniques to optimize data structure</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Delivery</h3>
                <p>Receive clean, AI-ready data with detailed analysis report</p>
              </div>
            </div>
          </section>

          <section className="databricks-showcase">
            <h2>Enterprise Data Pipeline Management</h2>
            <p>See how Databricks can manage your data pipeline with bronze, silver, and gold architecture for enterprise-scale operations like USPS.</p>
            <div className="databricks-cta">
              <a href="/databricks" className="databricks-btn">
                🏭 See Databricks in Action - USPS Case Study
              </a>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default DataPreparation;
